/**
 * Calendar Screen V2
 * Re-architected calendar with proper state management and separation of concerns
 *
 * Key improvements:
 * - Uses React Query for data fetching and caching
 * - Separates business logic into CalendarService
 * - Type-safe assignment model
 * - Auto-save with optimistic updates
 * - Session-independent recipe loading
 */

import React, { useState, useMemo, useEffect } from 'react'
import { useCalendarData, useUserId, useAuth, calendarService, formatDateKey, getWeekDates, MealType, MealAssignment, RecipeRecord, backendApiService, recipeApiService, ICONS } from '@talktoast/shared'
import { calendarExportService } from '@talktoast/shared'
import { Loading } from '@talktoast/ui'
import { CalendarHeader } from './CalendarHeader'
import { CalendarMobileView } from './CalendarMobileView'
import { RecipeDetailModal } from './RecipeDetailModal'
import { ArchiveRecipeModal } from './ArchiveRecipeModal'
import { MonthModal } from './MonthModal'
import { UnassignedMealsWarning } from './UnassignedMealsWarning'
import { MEAL_TYPES, getMealTypeConfig, getMealTypeColorClasses } from './calendarConstants'
import { showError, showWarning, showSuccess } from '../../utils/toast'

interface CalendarScreenV2Props {
  onBack?: () => void
  sessionId?: string | null
  onNavigateToSubscription?: () => void
}

export const CalendarScreenV2: React.FC<CalendarScreenV2Props> = ({
  onBack,
  sessionId,
  onNavigateToSubscription
}) => {
  const userId = useUserId()
  const { user, isGuest, signInWithGoogle } = useAuth()
  const [weekOffset, setWeekOffset] = useState(0)
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeRecord | null>(null)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])
  const [showRecipeModal, setShowRecipeModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [recipeToArchive, setRecipeToArchive] = useState<RecipeRecord | null>(null)
  const [showMonthModal, setShowMonthModal] = useState(false)
  const [showUnassignedWarning, setShowUnassignedWarning] = useState(false)
  const [isPushingToGoogle, setIsPushingToGoogle] = useState(false)
  const [showGoogleSignInPrompt, setShowGoogleSignInPrompt] = useState(false)
  // Desktop recipe selector modal state
  const [showRecipeSelectorModal, setShowRecipeSelectorModal] = useState(false)
  const [selectedDateForModal, setSelectedDateForModal] = useState<string | null>(null)
  const [selectedMealTypeForModal, setSelectedMealTypeForModal] = useState<MealType | null>(null)
  // Mobile selected date state (lifted from CalendarMobileView for week navigation sync)
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  })

  // Get date range for current week view
  const dateRange = useMemo(() => {
    return calendarService.getLoadDateRange()
  }, [])

  // Fetch calendar data with React Query
  const {
    assignments,
    recipes,
    unassignedRecipes,
    enabledMealTypes,
    isLoading,
    isError,
    error,
    assignRecipe,
    removeAssignment,
    isAssigning,
    isRemoving
  } = useCalendarData({
    userId,
    sessionId,
    dateRange,
    enabled: !!userId
  })

  // Calculate week dates
  const weekDates = useMemo(() => {
    const dates = getWeekDates(weekOffset)
    return dates.map(date => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const isToday = date.toDateString() === today.toDateString()
      const dayKey = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()]
      const dayLabel = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]
      const fullLabel = date.toLocaleDateString('en-US', { weekday: 'long' })

      return {
        key: dayKey,
        label: dayLabel,
        fullLabel,
        date: date.getDate(),
        fullDate: date,
        isToday,
        dateStr: formatDateKey(date)
      }
    })
  }, [weekOffset])

  // Check if there are any assigned meals for export
  const canExport = useMemo(() => {
    return assignments.size > 0
  }, [assignments])

  // Show push-to-Google button whenever there are assignments; eligibility checked on click
  const canPushToGoogle = useMemo(() => {
    return assignments.size > 0
  }, [assignments])

  // Handle calendar export (.ics file)
  const handleExport = () => {
    try {
      if (assignments.size === 0) {
        showWarning('No meals assigned to export')
        return
      }

      // Filter recipes that have assigned dates
      const assignedRecipes = recipes.filter(r =>
        r.assigned_dates && r.assigned_dates.length > 0
      )

      const icsContent = calendarExportService.generateICSFile(assignedRecipes)
      const weekStart = weekDates[0]?.fullDate
      const dateTag = weekStart
        ? weekStart.toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10)
      calendarExportService.downloadICSFile(icsContent, `meal-schedule-${dateTag}.ics`)
    } catch (err) {
      console.error('[CalendarV2] Export failed:', err)
      showError('Failed to export calendar. Please try again.')
    }
  }

  // Handle Google Calendar push
  const handlePushToGoogle = async () => {
    if (assignments.size === 0) {
      showWarning('No meals assigned to export')
      return
    }

    // Guests and non-Google users need to sign in with Google first
    if (isGuest || user?.app_metadata?.provider !== 'google') {
      setShowGoogleSignInPrompt(true)
      return
    }

    try {
      setIsPushingToGoogle(true)

      const assignedRecipes = recipes.filter(r =>
        r.assigned_dates && r.assigned_dates.length > 0
      )

      await calendarExportService.pushToGoogleCalendar(assignedRecipes)

      showSuccess('Successfully added meals to your Google Calendar!')
    } catch (err: any) {
      console.error('[CalendarV2] Google Calendar push failed:', err)

      // Expired provider token — guide user to re-authenticate
      if (err.message?.includes('sign back in') || err.message?.includes('access token not available')) {
        setShowGoogleSignInPrompt(true)
      } else {
        showError(err.message || 'Failed to push to Google Calendar')
      }
    } finally {
      setIsPushingToGoogle(false)
    }
  }

  // Handle done button
  const handleDone = async () => {
    // Check if there are unassigned recipes
    if (unassignedRecipes.length > 0) {
      setShowUnassignedWarning(true)
      return // Don't navigate yet - wait for user decision
    }

    // 100% scheduled - auto-complete the session
    if (sessionId) {
      try {
        await backendApiService.updateMealSessionStatus(sessionId, 'complete')
        console.log('[Calendar] Session auto-completed (100% scheduled)')
      } catch (error) {
        console.error('[Calendar] Failed to mark session complete:', error)
        // Continue navigation even if status update fails
      }
    }

    // Navigate back - assignments are auto-saved
    if (onBack) {
      onBack()
    } else {
      window.history.back()
    }
  }

  // Handle leaving with unassigned meals
  const handleLeaveAnyway = () => {
    setShowUnassignedWarning(false)
    // Session stays "scheduling" - don't update status
    if (onBack) {
      onBack()
    } else {
      window.history.back()
    }
  }

  // Handle recipe click
  const handleRecipeClick = (assignment: MealAssignment) => {
    const recipe = recipes.find(r => r.id === assignment.recipeId)
    if (recipe) {
      setSelectedRecipe(recipe)
      setShowRecipeModal(true)
    }
  }

  // Handle cell click (for recipe selection)
  const handleCellClick = (params: { date: string; mealType: MealType }) => {
    setSelectedDateForModal(params.date)
    setSelectedMealTypeForModal(params.mealType)
    setShowRecipeSelectorModal(true)
  }

  // Handle recipe selection from modal
  const handleRecipeSelect = (recipeId: string) => {
    if (!selectedDateForModal || !selectedMealTypeForModal) return

    assignRecipe({
      recipeId,
      date: selectedDateForModal,
      mealType: selectedMealTypeForModal
    })

    setShowRecipeSelectorModal(false)
    setSelectedDateForModal(null)
    setSelectedMealTypeForModal(null)
  }

  // Handle archive button click
  const handleArchiveClick = (recipe: RecipeRecord) => {
    setRecipeToArchive(recipe)
    setShowArchiveModal(true)
  }

  // Handle delete (didn't cook)
  const handleDelete = async (recipeId: string) => {
    try {
      await recipeApiService.deleteRecipe(recipeId)
      showSuccess('Recipe removed')
      // Refresh to remove from UI
      window.location.reload()
    } catch (error) {
      console.error('[Calendar] Failed to delete recipe:', error)
      showError('Failed to delete recipe')
    }
  }

  // Handle mark as cooked (cooked without scheduling)
  const handleMarkAsCooked = async (recipeId: string) => {
    try {
      const updatedRecipe = await recipeApiService.markRecipeAsCooked(recipeId, true)
      showSuccess('Recipe marked as cooked')

      // Show the recipe detail modal for rating
      setRecipeToArchive(null)
      setShowArchiveModal(false)
      setSelectedRecipe(updatedRecipe)
      setShowRecipeModal(true)

      // Note: Recipe stays in DB with is_cooked = true, but won't show in unassigned
      // because it's still there until scheduled. We keep it for history/rating.
    } catch (error) {
      console.error('[Calendar] Failed to mark recipe as cooked:', error)
      showError('Failed to mark recipe as cooked')
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading variant="spinner" size="lg" className="mx-auto" />
          <p className="text-gray-600 mt-4">Loading your meal schedule...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-gray-600 mb-6">
            {error?.message || 'Failed to load calendar data'}
          </p>
          <button
            onClick={onBack || (() => window.history.back())}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  // Empty state - no meals planned at all
  if (recipes.length === 0 && assignments.size === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">📅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            No Meals Planned Yet
          </h2>
          <p className="text-gray-600 mb-6">
            Start planning your weekly meals to see them here on your calendar
          </p>
          <button
            onClick={onBack || (() => window.history.back())}
            className="bg-toast-gold text-white px-6 py-3 rounded-lg hover:bg-toast-gold-dark transition-colors font-medium"
          >
            Plan Your Meals
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 lg:py-4">
          <CalendarHeader
            onBack={onBack}
            onExport={handleExport}
            onShowMonth={() => setShowMonthModal(true)}
            onDone={handleDone}
            canExport={canExport}
            canPushToGoogle={canPushToGoogle}
            onPushToGoogle={handlePushToGoogle}
            isPushingToGoogle={isPushingToGoogle}
          />
        </div>
      </div>

      {/* Main Content - Using single-day view for all screen sizes */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <CalendarMobileView
          weekDates={weekDates}
          assignments={assignments}
          unassignedRecipes={unassignedRecipes}
          enabledMealTypes={enabledMealTypes}
          onAssign={assignRecipe}
          onRemove={removeAssignment}
          onRecipeClick={handleRecipeClick}
          selectedDate={selectedDate}
          onSelectedDateChange={setSelectedDate}
        />
      </div>

      {/* Month Modal */}
      <MonthModal
        isOpen={showMonthModal}
        onClose={() => setShowMonthModal(false)}
        assignments={assignments}
        weekOffset={weekOffset}
        onNavigateToWeek={(newOffset, dateStr) => {
          setWeekOffset(newOffset)
          // Update selected date to the target date from week navigation
          setSelectedDate(dateStr)
        }}
        onRecipeClick={handleRecipeClick}
      />

      {/* Recipe Detail Modal */}
      <RecipeDetailModal
        recipe={selectedRecipe}
        isOpen={showRecipeModal}
        onClose={() => {
          setShowRecipeModal(false)
          setSelectedRecipe(null)
        }}
        onNavigateToSubscription={onNavigateToSubscription}
      />

      {/* Archive Recipe Modal */}
      <ArchiveRecipeModal
        recipe={recipeToArchive}
        isOpen={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false)
          setRecipeToArchive(null)
        }}
        onArchive={handleDelete}
        onArchiveAsCooked={handleMarkAsCooked}
      />

      {/* Recipe Selector Modal (Desktop) */}
      {showRecipeSelectorModal && selectedMealTypeForModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setShowRecipeSelectorModal(false)
              setSelectedDateForModal(null)
              setSelectedMealTypeForModal(null)
            }}
          />

          {/* Modal */}
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[80vh] flex flex-col mx-4">
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">
                  {MEAL_TYPES.find(mt => mt.key === selectedMealTypeForModal)?.emoji}
                </span>
                <h2 className="text-xl font-bold">
                  Select {MEAL_TYPES.find(mt => mt.key === selectedMealTypeForModal)?.label}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowRecipeSelectorModal(false)
                  setSelectedDateForModal(null)
                  setSelectedMealTypeForModal(null)
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Recipe List */}
            <div className="flex-1 overflow-y-auto p-6">
              {unassignedRecipes
                .filter(recipe => recipe.recipe_data.mealType === selectedMealTypeForModal)
                .length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg font-medium mb-2">No recipes available</p>
                  <p className="text-sm">All {MEAL_TYPES.find(mt => mt.key === selectedMealTypeForModal)?.label.toLowerCase()} recipes have been assigned.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {unassignedRecipes
                    .filter(recipe => recipe.recipe_data.mealType === selectedMealTypeForModal)
                    .map(recipe => (
                      <button
                        key={recipe.id}
                        onClick={() => handleRecipeSelect(recipe.id)}
                        className="text-left bg-gray-50 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-400 rounded-lg p-4 transition-all"
                      >
                        <div className="font-semibold text-gray-900 mb-1">
                          {recipe.recipe_data.name}
                        </div>
                        {recipe.recipe_data.description && (
                          <div className="text-sm text-gray-600 line-clamp-2">
                            {recipe.recipe_data.description}
                          </div>
                        )}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Google Sign-In Prompt */}
      {showGoogleSignInPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <ICONS.navigation.calendar className="w-8 h-8 text-blue-600 shrink-0" />
              <button
                onClick={() => setShowGoogleSignInPrompt(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <ICONS.actions.close className="w-5 h-5" />
              </button>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Connect Google Calendar
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              {isGuest
                ? 'Sign in with Google to push your meal schedule directly to Google Calendar.'
                : 'Your Google Calendar access has expired. Please sign in with Google again to reconnect.'}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowGoogleSignInPrompt(false)
                  signInWithGoogle()
                }}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <ICONS.navigation.calendar className="w-4 h-4" />
                Sign in with Google
              </button>
              <button
                onClick={() => setShowGoogleSignInPrompt(false)}
                className="w-full py-2.5 px-4 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unassigned Meals Warning Modal */}
      <UnassignedMealsWarning
        open={showUnassignedWarning}
        unassignedRecipes={unassignedRecipes}
        assignedCount={recipes.length - unassignedRecipes.length}
        onStay={() => setShowUnassignedWarning(false)}
        onLeave={handleLeaveAnyway}
      />
    </div>
  )
}

export default CalendarScreenV2
